import { useTranslation } from 'react-i18next';
import React, { useState } from 'react';
import Typography from '@mui/material/Typography';
import { useUploadProductImagesMutation } from '../../../graphql/mutation/product/productImage/uploadProductImagesMutation';
import { useUnpublishProductImagesMutation } from '../../../graphql/mutation/product/productImage/unpublishProductImagesMutation';
import { useDeleteProductImagesMutation } from '../../../graphql/mutation/product/productImage/deleteProductImagesMutation';
import { usePublishProductImagesMutation } from '../../../graphql/mutation/product/productImage/publishProductImagesMutation';
import BaseImageUpload from './_module/BaseImageUpload';
import useNotification from '../_hook/useNotification';
import { useReorderProductImagesMutation } from '../../../graphql/mutation/product/productImage/reorderProductImagesMutation';

interface IProps {
  productId: string;
  images: {
    status: boolean;
    filename: string;
    filename_large?: string;

    filenameUrl?: string;
    filenameLargeUrl?: string;
  }[];
}

export default function ProductImageUpload(props: IProps) {
  const { productId, images } = props;
  const [uploadingCount, setUploadingCount] = useState(0);
  const { t } = useTranslation();
  const { notification } = useNotification();
  const queryFragment = `updatedProduct {
				_id
                media {
                    images {
                        status
                        filename
                        filename_large
                        
                        filenameUrl
                        filenameLargeUrl
                    }
                }
			}`;
  const [uploadProductImagesMutation] = useUploadProductImagesMutation(
    queryFragment,
    {
      onCompleted: () => {
        setUploadingCount(0);
      },
      onError: (e) => {
        setUploadingCount(0);
        notification.error(e.message);
      },
    }
  );
  const [deleteProductImagesMutation] = useDeleteProductImagesMutation(
    queryFragment,
    {
      onError: (e) => {
        notification.error(e.message);
      },
    }
  );
  const [publishProductImagesMutation] = usePublishProductImagesMutation(
    queryFragment,
    {
      onError: (e) => {
        notification.error(e.message);
      },
    }
  );
  const [unpublishProductImagesMutation] = useUnpublishProductImagesMutation(
    queryFragment,
    {
      onError: (e) => {
        notification.error(e.message);
      },
    }
  );
  const [reorderProductImagesMutation] = useReorderProductImagesMutation(
    queryFragment,
    {
      onError: (e) => {
        notification.error(e.message);
      },
    }
  );

  // 200KB
  const maximumUploadSize = 204800;
  const acceptedUploadFileTypes = 'image/png, image/jpeg, image/jpg';

  function onUpload(fileList: FileList | null) {
    if (fileList && fileList.length) {
      setUploadingCount(uploadingCount + fileList.length);
      uploadProductImagesMutation({
        variables: {
          _id: productId,
          images: fileList,
        },
      });
    }
  }

  return (
    <>
      <Typography variant="h6">
        {t('upload')} {t('image')}
      </Typography>
      {images && !images.length && (
        <Typography variant="subtitle2" color="red">
          **{t('seo_product_image_helper_text')}
        </Typography>
      )}
      <BaseImageUpload
        images={images}
        color="primary"
        maximumUploadSize={maximumUploadSize}
        displayImageSize="large"
        gridXs={12}
        gridSm={6}
        gridMd={3}
        gridLg={3}
        gridXl={2}
        uploadInfo={{
          ratio: '1 : 1',
          size: '200KB',
          acceptedUploadFileTypes,
        }}
        uploadingCount={uploadingCount}
        onUpload={onUpload}
        onDelete={(filename) => {
          deleteProductImagesMutation({
            variables: {
              _id: productId,
              imageFilenames: [filename],
            },
          });
        }}
        onPublish={(filenames) => {
          publishProductImagesMutation({
            variables: {
              _id: productId,
              filenames,
            },
          });
        }}
        onUnpublish={(filenames) => {
          unpublishProductImagesMutation({
            variables: {
              _id: productId,
              filenames,
            },
          });
        }}
        onReorder={(filenames) => {
          reorderProductImagesMutation({
            variables: {
              _id: productId,
              filenames,
            },
          });
        }}
      />
    </>
  );
}
